import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Button } from '@/components/ui/button'
import { StandardSectionExtractButton } from './StandardSectionExtractButton'
import type { StandardSectionExtractionState } from '../hooks/useStandardSectionExtraction'

type StandardSectionEmptyStateProps = {
  icon: ReactNode
  title: string
  description: string
  hasPdf: boolean
  isPdfUploading?: boolean
  extraction: StandardSectionExtractionState
  onUploadPdf?: () => void
  onManualAction?: () => void
  manualActionLabel?: string
}

export function StandardSectionEmptyState({
  icon,
  title,
  description,
  hasPdf,
  isPdfUploading = false,
  extraction,
  onUploadPdf,
  onManualAction,
  manualActionLabel,
}: StandardSectionEmptyStateProps) {
  const { t } = useTranslation('standardsLibrary')
  const { openExtractDialog } = extraction

  return (
    <Empty className='border-t py-10'>
      <EmptyHeader>
        <EmptyMedia variant='icon'>{icon}</EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <div className='flex flex-wrap justify-center gap-2'>
          {!hasPdf && onUploadPdf != null && (
            <Button
              type='button'
              size='sm'
              onClick={onUploadPdf}
              disabled={isPdfUploading}
            >
              <Upload className='mr-2 size-4' aria-hidden />
              {t('detail.empty.uploadPdf')}
            </Button>
          )}
          {hasPdf && onManualAction != null && manualActionLabel != null && (
            <Button type='button' size='sm' onClick={onManualAction}>
              {manualActionLabel}
            </Button>
          )}
          {hasPdf && (
            <StandardSectionExtractButton
              extraction={extraction}
              onClick={openExtractDialog}
              isExternalTrigger
            />
          )}
        </div>
      </EmptyContent>
    </Empty>
  )
}
